import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ROUTE_MAIN_PATHS } from '@app/app.routes.paths';
import { environment } from '@environments/_current/environment';
import {
  DecodedAaptivToken,
  LocalStorageKey,
  LoginResponse,
} from '@skyfit/core-data-library';
import jwtDecode from 'jwt-decode';
import { catchError, map, Observable, of, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AdminAuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  lockedOutDurationMs = 300_000;
  superAdminRole = 'ADMIN';
  tenantAdminRole = 'ENTERPRISE_GROUP_ADMIN';
  allowedRoles = [this.tenantAdminRole, this.superAdminRole];

  getToken() {
    return localStorage.getItem(LocalStorageKey.accessToken);
  }

  getUserId(): string | undefined {
    const token = this.getToken();
    if (!token) {
      return undefined;
    }
    try {
      const decodedToken = jwtDecode(token) as DecodedAaptivToken;
      return decodedToken?.uip?.user_object_id;
    } catch {
      return undefined;
    }
  }

  setToken(token: string) {
    localStorage.setItem(LocalStorageKey.accessToken, token);
  }

  clearToken() {
    localStorage.removeItem(LocalStorageKey.accessToken);
  }

  clearTokenIfExpired() {
    const token = localStorage.getItem(LocalStorageKey.accessToken);
    if (token) {
      const decodedToken = jwtDecode(token) as DecodedAaptivToken;
      const currentTime = Math.floor(Date.now() / 1000);
      if (decodedToken?.exp && decodedToken.exp < currentTime) {
        localStorage.removeItem(LocalStorageKey.accessToken);
      }
    }
  }

  isLoggedIn() {
    return !!this.getToken();
  }

  isAdmin(roles: string[]) {
    if (!roles) {
      return false;
    }
    return roles.some((role) => this.allowedRoles.includes(role));
  }

  isSuperAdmin(roles: string[]) {
    if (!roles?.includes(this.superAdminRole)) {
      return false;
    }
    return true;
  }

  getLockedOutTimeLeft() {
    const lockedOutData = localStorage.getItem(LocalStorageKey.userLoginLock);
    if (!lockedOutData) {
      return 0;
    }

    const lockedOutAt = parseInt(lockedOutData);
    const dt = Date.now() + 1; // adding 1ms to prevent 0 min to return.
    const lockedOutMin = this.lockedOutDurationMs / (1000 * 60);
    return Math.ceil(lockedOutMin - (dt - lockedOutAt) / (1000 * 60));
  }

  isLockedOut() {
    const lockedOutData = localStorage.getItem(LocalStorageKey.userLoginLock);
    if (!lockedOutData) {
      return false;
    }

    const lockedOutAt = parseInt(lockedOutData);
    const dt = Date.now();
    if (lockedOutAt && dt - lockedOutAt > this.lockedOutDurationMs) {
      this.clearLockedOut();
      return false;
    } else {
      return true;
    }
  }

  setLockedOut() {
    localStorage.setItem(LocalStorageKey.userLoginLock, String(Date.now()));
  }

  clearLockedOut() {
    localStorage.removeItem(LocalStorageKey.userLoginLock);
  }

  /**
   * Currently, we are using the endpoint where any user can login,
   * so we need to check the roles array and making sure that a token
   * is being returned.
   *
   * This needs to change! We need an admin endpoint for logging in
   * only admins.
   */
  login(email: string, password: string, preAdmin?: boolean): Observable<any> {
    if (this.isLockedOut()) {
      return of({
        code: 429,
      });
    }

    return this.http
      .post(`${environment.aaptivServiceUrl}/api/v3/login`, { email, password })
      .pipe(
        tap((data: LoginResponse) => {
          if ((this.isAdmin(data?.roles) || preAdmin) && data?.secret) {
            this.setToken(data.secret);
          }
        }),
        map((data: LoginResponse) => {
          /**
           * Support for current response is to react to 200 with data that inlcudes an
           * error message. This is not ideal and should be changed.
           */
          if (
            (data as any).error &&
            (data as any).error === 'Incorrect credentials'
          ) {
            return {
              code: 401,
            };
          }
          if (!this.isAdmin(data?.roles) && !preAdmin) {
            return {
              code: 403,
            };
          }

          return {
            code: 200,
            data: data,
          };
        }),
        catchError((err) => {
          if (err.status === 429) {
            this.setLockedOut();
          }

          return of({
            code: err.status,
          });
        })
      );
  }

  forgotPassword(email: string): Observable<any> {
    const url = `${environment.aaptivServiceUrl}/api/v1/passwordReset`;
    return this.http.post(url, { email }).pipe(
      map((data: LoginResponse) => {
        // Test the 500 from the server.
        if ((data as any).error) {
          return {
            code: 400,
          };
        }

        return {
          code: 204,
        };
      }),
      catchError((err) => {
        return of({
          code: err.status,
        });
      })
    );
  }

  signOutAndRedirect() {
    this.clearToken();
    this.router.navigate([ROUTE_MAIN_PATHS.login]);
  }

  userUpdatePassword(
    currentPassword: string,
    newPassword: string
  ): Observable<any> {
    const url = `${environment.aaptivServiceUrl}/api/v1/user/password`;
    return this.http.put(url, { currentPassword, newPassword });
  }
}
